/**
 * Quick entry for the Daily Report: what a person types fast becomes what the
 * sheet means — applied when a field is left, and again by the P&L when it
 * reads a sheet, so a report typed before this existed still prices.
 *
 *   times    '330'  -> '3:30 PM'   '7' -> '7:00 AM'   '1530' -> '3:30 PM'
 *   amounts  '12.3' -> '12.3 m3'   deliveries '78.48' -> '78.48 MTons'
 *
 * Both are PURE and only ever touch a bare entry: anything already carrying
 * AM/PM, a colon, a unit, or that is not a number at all is returned exactly
 * as typed. Nothing here can turn a deliberate value into a different one.
 *
 * The AM/PM guess for a bare hour follows a concrete crew's day: 7–11 is
 * morning, 12–6 is afternoon. Typing 'am' or 'pm' overrides it; 24-hour
 * entries (13–23) need no guess.
 */

/** Hours a bare entry reads as morning. Everything else (1–6, 12) is PM. */
const MORNING_HOURS = new Set([7, 8, 9, 10, 11]);

function clock(hour24: number, minute: number): string {
  const meridiem = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${meridiem}`;
}

/**
 * '330' -> '3:30 PM'. Accepts 1–4 digits, optionally followed by am/pm, with
 * or without a colon. Anything else is returned untouched.
 */
export function quickTime(text: string): string {
  // '5:pm' — a colon with nothing after it but the meridiem — is a real habit.
  const raw = (text ?? '').trim().replace(/:\s*(?=[aApP][mM]?$)/, ' ');
  const match = /^(\d{1,2})(?::?(\d{2}))?\s*([aApP][mM]?)?$/.exec(raw);
  if (!match) return text;

  let hour = Number(match[1]);
  const minute = match[2] === undefined ? 0 : Number(match[2]);
  const said = match[3]?.toLowerCase();
  if (minute > 59) return text;

  if (said) {
    // The person said which; honour it. '12am' is midnight, '12pm' is noon.
    if (hour < 1 || hour > 12) return text;
    if (said.startsWith('a')) hour = hour === 12 ? 0 : hour;
    else hour = hour === 12 ? 12 : hour + 12;
  } else if (hour >= 13 && hour <= 23) {
    // 24-hour entry: nothing to guess.
  } else if (hour >= 1 && hour <= 12) {
    hour = MORNING_HOURS.has(hour) ? hour : hour === 12 ? 12 : hour + 12;
  } else {
    return text;
  }

  return clock(hour, minute);
}

/**
 * '12.3' -> '12.3 m3'. A bare number gets the unit; anything else — including
 * a number that already carries one — is returned untouched. The unit is
 * written 'm3', matching what the sheet already holds and what the P&L reads.
 */
export function quickCubes(text: string): string {
  const raw = (text ?? '').trim();
  if (!/^\d+(?:\.\d+)?$/.test(raw)) return text;
  return `${raw} m3`;
}

/**
 * '78.48' -> '78.48 MTons'. The delivery half of quickCubes: a delivery is
 * weighed, not measured, and 'MTons' is the spelling already on every sheet
 * and the one the P&L reads as tonnes.
 */
export function quickTonnes(text: string): string {
  const raw = (text ?? '').trim();
  if (!/^\d+(?:\.\d+)?$/.test(raw)) return text;
  return `${raw} MTons`;
}
